import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useCartCount } from '../context/CartCountContext';
import { formatRelativeTime } from '../utils/time';
import './ChatWidget.css';

interface ChatMessage {
  id: string;
  senderRole: 'user' | 'admin' | 'assistant';
  content: string;
  createdAt: string;
}

const ChatWidget: React.FC = () => {
  const { user } = useAuth();
  const { cartHasItems, refreshCartCount } = useCartCount();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState('');
  const [smartReplies, setSmartReplies] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);
  const portalRootRef = useRef<HTMLDivElement | null>(null);
  const [portalRoot, setPortalRoot] = useState<HTMLDivElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [wsReady, setWsReady] = useState(false);

  const guestId = useMemo(() => {
    if (user) return null;
    const existing = localStorage.getItem('guestChatId');
    if (existing) return existing;
    const generated =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `guest-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem('guestChatId', generated);
    return generated;
  }, [user]);

  const shouldShow = useMemo(() => !user?.isAdmin, [user]);

  const scrollToBottom = () => {
    if (!messagesContainerRef.current) return;
    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
  };


  // const fetchSmartReplies = useCallback(async () => {
  //   if (smartLoading) return;
  //   setSmartLoading(true);
  //   try {
  //     const response = user
  //       ? await api.get("/chats/me/smart-replies")
  //       : await api.get("/guest-chats/me/smart-replies", {
  //           headers: { "x-guest-id": guestId || "" },
  //         });
  //     setSmartReplies(response.data?.replies || []);
  //   } catch (error) {
  //     setSmartReplies([]);
  //   } finally {
  //     setSmartLoading(false);
  //   }
  // }, [guestId, smartLoading, user]);

  const fetchSmartReplies = useCallback(() => {
    setSmartReplies([]);
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const response = user
        ? await api.get('/chats/me')
        : await api.get('/guest-chats/me', {
            headers: { 'x-guest-id': guestId || '' },
          });
      setMessages(response.data?.messages || []);
      if (user) {
        refreshCartCount();
      }
      fetchSmartReplies();
    } finally {
      setLoading(false);
    }
  }, [fetchSmartReplies, guestId, refreshCartCount, user]);

  const getWsUrl = useCallback(() => {
    const apiUrl = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.host}/api`;
    const base = apiUrl.replace(/\/api\/?$/, '');
    return base.replace(/^http/, 'ws') + '/ws';
  }, []);

  useEffect(() => {
    if (!open) return;
    fetchMessages();
    if (wsReady) return;
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [fetchMessages, open, wsReady]);

  useEffect(() => {
    if (!open) return;
    const socket = new WebSocket(getWsUrl());
    wsRef.current = socket;
    setWsReady(false);

    socket.onopen = () => {
      const payload = user
        ? { type: 'identify', role: 'user' }
        : { type: 'identify', role: 'user', guestId: guestId || undefined };
      socket.send(JSON.stringify(payload));
    };

    socket.onmessage = event => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'ready') {
          setWsReady(true);
          setMessages(data.messages || []);
          return;
        }
        if (data.type === 'message') {
          if (data.clientId) {
            setMessages(prev =>
              prev.map(message => (message.id === data.clientId ? data.message : message))
            );
          } else if (data.message) {
            setMessages(prev => [...prev, data.message]);
          }
          if (Array.isArray(data.assistantMessages) && data.assistantMessages.length) {
            setMessages(prev => [...prev, ...data.assistantMessages]);
          }
          if (user) {
            refreshCartCount();
          }
          fetchSmartReplies();
        }
      } catch (error) {
        // Ignore malformed events
      }
    };

    socket.onclose = () => {
      setWsReady(false);
    };

    return () => {
      socket.close();
      wsRef.current = null;
    };
  }, [fetchSmartReplies, getWsUrl, guestId, open, refreshCartCount, user]);

  useEffect(() => {
    if (portalRootRef.current) return;
    const existing = document.getElementById('chat-widget-root');
    if (existing) {
      portalRootRef.current = existing as HTMLDivElement;
      setPortalRoot(portalRootRef.current);
      return;
    }
    const root = document.createElement('div');
    root.id = 'chat-widget-root';
    root.style.position = 'fixed';
    root.style.inset = '0';
    root.style.zIndex = '2147483647';
    root.style.pointerEvents = 'none';
    root.style.isolation = 'isolate';
    document.body.appendChild(root);
    portalRootRef.current = root;
    setPortalRoot(root);
    return () => {
      if (portalRootRef.current) {
        document.body.removeChild(portalRootRef.current);
        portalRootRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    if (shouldStickToBottomRef.current) {
      scrollToBottom();
    }
  }, [messages, open]);

  const handleSend = async (override?: string) => {
    const content = (override ?? input).trim();
    if (!content) return;

    const tempId = `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const tempMessage: ChatMessage = {
      id: tempId,
      senderRole: 'user',
      content,
      createdAt: new Date().toISOString(),
    };

    setSending(true);
    setInput('');
    setMessages(prev => [...prev, tempMessage]);
    try {
      if (wsRef.current && wsReady) {
        wsRef.current.send(JSON.stringify({ type: 'message', content, clientId: tempId }));
      } else {
        const response = user
          ? await api.post('/chats/me/messages', { content })
          : await api.post(
              '/guest-chats/me/messages',
              { content },
              { headers: { 'x-guest-id': guestId || '' } }
            );
        const newMessages: ChatMessage[] = [];
        if (response.data?.message) {
          setMessages(prev =>
            prev.map(message => (message.id === tempId ? response.data.message : message))
          );
        }
        if (Array.isArray(response.data?.assistantMessages)) {
          newMessages.push(...response.data.assistantMessages);
        } else if (response.data?.assistantMessage) {
          newMessages.push(response.data.assistantMessage);
        }
        if (newMessages.length > 0) {
          setMessages(prev => [...prev, ...newMessages]);
        } else {
          await fetchMessages();
        }
        if (user) {
          refreshCartCount();
        }
        fetchSmartReplies();
      }
    } catch (error) {
      setMessages(prev => prev.filter(message => message.id !== tempId));
      setInput(content);
    } finally {
      setSending(false);
    }
  };

  if (!shouldShow) return null;

  const content = (
    <div className="chat-widget">
      {open && (
        <div className="chat-panel">
          <div className="chat-header">
            <div>
              <h4>Concierge Support</h4>
              <p>We typically reply within a minute.</p>
            </div>
            <span className={`chat-status ${wsReady ? 'online' : 'offline'}`}>
              {wsReady ? 'Connected' : 'Connecting'}
            </span>
            <button className="chat-close" onClick={() => setOpen(false)} aria-label="Close chat">
              ×
            </button>
          </div>
          <div
            className="chat-messages"
            ref={messagesContainerRef}
            onScroll={() => {
              const container = messagesContainerRef.current;
              if (!container) return;
              const distanceToBottom =
                container.scrollHeight - container.scrollTop - container.clientHeight;
              shouldStickToBottomRef.current = distanceToBottom < 24;
            }}
          >
            {loading ? (
              <div className="chat-loading">Loading conversation...</div>
            ) : (
              messages.map(message => (
                <div
                  key={message.id}
                  className={`chat-message chat-message-${message.senderRole}`}
                >
                  <div className="chat-bubble">{message.content}</div>
                  <div className="chat-time">
                    {formatRelativeTime(message.createdAt)}
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>
          {smartReplies.length > 0 && (
            <div className="chat-smart-replies">
              {smartReplies.map(reply => (
                <button
                  key={reply}
                  type="button"
                  className="chat-smart-reply"
                  onClick={() => handleSend(reply)}
                  disabled={sending}
                >
                  {reply}
                </button>
              ))}
            </div>
          )}
          <div className="chat-input">
            <textarea
              placeholder="Type your message..."
              value={input}
              onChange={event => setInput(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  const trimmed = input.trim();
                  if (!trimmed) return;
                  setInput('');
                  handleSend(trimmed);
                }
              }}
              rows={2}
              disabled={sending}
            />
            {sending && <div className="chat-sending">Sending...</div>}
            {cartHasItems && (
              <button
                type="button"
                className="chat-icon-button chat-icon-checkout"
                onClick={() => handleSend('checkout')}
                disabled={sending}
                aria-label="Checkout"
                title="Checkout"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M6 6h14l-2 8H8L6 6zm-2 0h2l2.2 9.2c.1.5.5.8 1 .8h9.5a1 1 0 0 0 1-.8L22 4H6.4L5.7 1.9A1 1 0 0 0 4.7 1H2v2h1.3L4 6zm4 16a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            )}
            <button
              className="chat-icon-button chat-icon-send"
              onClick={() => handleSend()}
              disabled={sending || !input.trim()}
              aria-label="Send message"
              title="Send"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M2 21l20-9L2 3v7l14 2-14 2v7z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
      <button className="chat-bubble-button" onClick={() => setOpen(!open)} aria-label="Open chat">
        💬
      </button>
    </div>
  );
  return createPortal(content, portalRoot || document.body);
};

export default ChatWidget;
